// Incremental Server-Sent Events frame parser (ADR-0050 FR-16).
//
// No DOM, no fetch, no globals: a string goes in, frames come out. This is the
// one new algorithm in this increment, and the one whose failures a browser
// only shows when the network stutters — which is why it lives here, on its
// own, with unit tests, rather than as a helper inside chat.js.

/**
 * Parse one raw frame body into {event, data}.
 *
 * Returns null for anything it does not understand — a comment line, a
 * keep-alive, a malformed payload — rather than throwing: a stream must not
 * die on a frame it could simply skip.
 *
 * @returns {{event: string, data: object}|null}
 */
function parseFrame(raw) {
    let event = null;
    let data = null;
    for (const line of raw.split('\n')) {
        if (line.startsWith('event: ')) {
            event = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
            data = line.slice(6);
        }
    }
    if (!event || data === null) return null;
    try {
        return { event, data: JSON.parse(data) };
    } catch (error) {
        return null;
    }
}

/**
 * Incremental frame parser.
 *
 * A network chunk boundary has nothing to do with a frame boundary: one chunk
 * may hold three frames, and one frame may be split across two chunks in the
 * middle of a word. The parser holds a buffer and emits only complete frames —
 * everything after the last blank line stays for the next push().
 */
export function createFrameParser() {
    let buffer = '';
    return {
        /** @returns {{event: string, data: object}[]} complete frames only */
        push(chunk) {
            buffer += chunk;
            const frames = [];
            let index;
            while ((index = buffer.indexOf('\n\n')) !== -1) {
                const raw = buffer.slice(0, index);
                buffer = buffer.slice(index + 2);
                const frame = parseFrame(raw);
                if (frame) frames.push(frame);
            }
            return frames;
        },
        /** True when a partial frame is still buffered — an abort mid-frame. */
        hasPending() {
            return buffer.length > 0;
        }
    };
}

/**
 * Accumulates `text` deltas into the answer as written so far.
 *
 * Separate from the parser because it is what the render throttle reads, and
 * because the concatenation strategy is the thing most likely to change.
 */
export function createAccumulator() {
    const parts = [];
    return {
        append(delta) {
            parts.push(delta);
            return parts.join('');
        },
        text() {
            return parts.join('');
        }
    };
}
